/**
 * Tokens Page
 * Displays user's token balance, statistics, and transaction history
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTokens } from '../contexts/TokenContext';
import { getTokenTransactions, formatTokenAmount } from '../services/tokenService';
import { TokenTransaction, TokenTransactionCategory } from '../types/tokens';
import RewardedAdPlayer from '../components/ads/RewardedAdPlayer';
import './TokensPage.css';

type FilterCategory = 'all' | TokenTransactionCategory;

const TokensPage: React.FC = () => {
    const { t } = useTranslation('tokens');
    const { stats, isLoading: statsLoading, claimDaily, requestAdNonce, watchAdForTokens } = useTokens();

    const [transactions, setTransactions] = useState<TokenTransaction[]>([]);
    const [isLoadingTx, setIsLoadingTx] = useState(false);
    const [filter, setFilter] = useState<FilterCategory>('all');
    const [hasMore, setHasMore] = useState(false);
    const [offset, setOffset] = useState(0);
    const [isClaiming, setIsClaiming] = useState(false);
    const [showAdPlayer, setShowAdPlayer] = useState(false);
    const [adNonce, setAdNonce] = useState<string | null>(null);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const LIMIT = 20;

    // Load transactions
    const loadTransactions = useCallback(async (reset = false) => {
        setIsLoadingTx(true);
        try {
            const newOffset = reset ? 0 : offset;
            const result = await getTokenTransactions({
                limit: LIMIT,
                offset: newOffset,
                category: filter === 'all' ? undefined : filter,
            });

            if (reset) {
                setTransactions(result.transactions);
            } else {
                setTransactions(prev => [...prev, ...result.transactions]);
            }
            setHasMore(result.pagination.hasMore);
            setOffset(newOffset + result.transactions.length);
        } catch (err) {
            console.error('Failed to load transactions:', err);
        } finally {
            setIsLoadingTx(false);
        }
    }, [filter, offset]);

    // Load initial transactions
    useEffect(() => {
        loadTransactions(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset on filter change; loadTransactions(true) ignores offset
    }, [filter]);

    // Handle daily claim
    const handleClaimDaily = async () => {
        if (isClaiming || !stats?.canClaimDailyLogin) return;

        setIsClaiming(true);
        setMessage(null);
        try {
            const result = await claimDaily();
            setMessage({
                type: 'success',
                text: t('claimSuccess', { amount: result.earned, streak: result.streak }),
            });
            loadTransactions(true);
        } catch (err) {
            setMessage({ type: 'error', text: t('claimFailed') });
        } finally {
            setIsClaiming(false);
        }
    };

    // Handle watch ad - get nonce first, then open ad player
    const handleWatchAd = async () => {
        if (!stats || stats.dailyAdTokensRemaining <= 0) return;
        setMessage(null);
        try {
            const nonce = await requestAdNonce();
            setAdNonce(nonce);
            setShowAdPlayer(true);
        } catch (err) {
            setMessage({ type: 'error', text: t('adFailed') });
        }
    };

    // Handle ad reward earned
    const handleAdRewardEarned = useCallback(async () => {
        if (!adNonce) return;
        try {
            const result = await watchAdForTokens(adNonce);
            setAdNonce(null);
            setMessage({
                type: 'success',
                text: t('adSuccess', { amount: result.earned }),
            });
            loadTransactions(true);
        } catch (err) {
            setMessage({ type: 'error', text: t('adFailed') });
        }
    }, [t, adNonce, watchAdForTokens, loadTransactions]);

    // Format date with locale awareness
    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Translate transaction description
    const translateDescription = (tx: TokenTransaction): string => {
        const desc = tx.description || '';

        // Quest gate descriptions (course and standalone)
        if (tx.referenceType === 'quest_gate') {
            // Extract gate type from various formats
            const gateTypeMap: Record<string, string> = {
                translation: t('questGate.translation'),
                combine: t('questGate.combine'),
                expansion: t('questGate.expansion'),
                essay: t('questGate.essay'),
            };
            for (const [key, label] of Object.entries(gateTypeMap)) {
                if (desc.toLowerCase().includes(key)) {
                    return label;
                }
            }
            return desc;
        }

        // Ad reward
        if (desc === 'Watched rewarded ad') {
            return t('desc.watchedAd');
        }

        // Daily login bonus
        const dailyMatch = desc.match(/Daily login bonus \(Day (\d+)\)/);
        if (dailyMatch) {
            return t('desc.dailyLogin', { day: dailyMatch[1] });
        }

        // Daily goal
        if (desc === 'Completed daily learning goal') {
            return t('desc.dailyGoal');
        }

        // AI question
        const aiMatch = desc.match(/AI question \(free: (\d+) remaining\)/);
        if (aiMatch) {
            return t('desc.aiQuestion', { remaining: aiMatch[1] });
        }

        return desc;
    };

    if (statsLoading) {
        return (
            <div className="tokens-page">
                <div className="tokens-loading">
                    <div className="loading-spinner" />
                    <p>{t('loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="tokens-page">
            <div className="tokens-container">
                {/* Header */}
                <div className="tokens-header">
                    <h1>{t('title')}</h1>
                    <p className="tokens-subtitle">{t('subtitle')}</p>
                </div>

                {/* Balance Card */}
                <div className="tokens-balance-card">
                    <div className="balance-main">
                        <div className="balance-icon">
                            {/* Loopy Token Icon - L in coin */}
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <circle cx="12" cy="12" r="7" strokeOpacity="0.4" strokeWidth="1"/>
                                <path d="M9 7v10h6" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <div className="balance-info">
                            <span className="balance-label">{t('currentBalance')}</span>
                            <span className="balance-value">{stats?.balance.toLocaleString() || 0}</span>
                        </div>
                    </div>

                    <div className="balance-stats">
                        <div className="stat-item">
                            <span className="stat-label">{t('totalEarned')}</span>
                            <span className="stat-value positive">+{stats?.totalEarned.toLocaleString() || 0}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">{t('totalSpent')}</span>
                            <span className="stat-value negative">-{stats?.totalSpent.toLocaleString() || 0}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">{t('loginStreak')}</span>
                            <span className="stat-value">{stats?.loginStreak || 0} {t('days')}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">{t('bestStreak')}</span>
                            <span className="stat-value">{stats?.bestLoginStreak || 0} {t('days')}</span>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="tokens-actions">
                    <button
                        className={`action-btn daily-btn ${!stats?.canClaimDailyLogin ? 'disabled' : ''}`}
                        onClick={handleClaimDaily}
                        disabled={isClaiming || !stats?.canClaimDailyLogin}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        <span>
                            {isClaiming ? t('claiming') : (stats?.canClaimDailyLogin ? t('claimDaily') : t('alreadyClaimed'))}
                        </span>
                    </button>

                    <button
                        className={`action-btn ad-btn ${!stats || stats.dailyAdTokensRemaining <= 0 ? 'disabled' : ''}`}
                        onClick={handleWatchAd}
                        disabled={!stats || stats.dailyAdTokensRemaining <= 0}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="5 3 19 12 5 21 5 3"/>
                        </svg>
                        <span>
                            {t('watchAd', { remaining: stats?.dailyAdTokensRemaining || 0 })}
                        </span>
                    </button>
                </div>

                {/* Message */}
                {message && (
                    <div className={`tokens-message ${message.type}`}>
                        {message.text}
                    </div>
                )}

                {/* Transaction History */}
                <div className="tokens-history">
                    <div className="history-header">
                        <h2>{t('transactionHistory')}</h2>
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value as FilterCategory)}
                            className="history-filter"
                        >
                            <option value="all">{t('filter.all')}</option>
                            <optgroup label={t('filter.earning')}>
                                <option value="earn_ad">{t('filter.earnAd')}</option>
                                <option value="earn_engagement">{t('filter.earnEngagement')}</option>
                                <option value="earn_referral">{t('filter.earnReferral')}</option>
                                <option value="earn_subscription">{t('filter.earnSubscription')}</option>
                                <option value="earn_event">{t('filter.earnEvent')}</option>
                                <option value="earn_purchase">{t('filter.earnPurchase')}</option>
                            </optgroup>
                            <optgroup label={t('filter.spending')}>
                                <option value="spend_basic_feature">{t('filter.spendBasic')}</option>
                                <option value="spend_advanced_feature">{t('filter.spendAdvanced')}</option>
                                <option value="spend_content">{t('filter.spendContent')}</option>
                                <option value="spend_social">{t('filter.spendSocial')}</option>
                            </optgroup>
                        </select>
                    </div>

                    {transactions.length === 0 && !isLoadingTx ? (
                        <div className="history-empty">
                            <p>{t('noTransactions')}</p>
                        </div>
                    ) : (
                        <div className="history-list">
                            {transactions.map((tx) => (
                                <div key={tx.id} className={`history-item ${tx.amount >= 0 ? 'earn' : 'spend'}`}>
                                    <div className="history-item-icon">
                                        {tx.amount >= 0 ? (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="12" y1="19" x2="12" y2="5"/>
                                                <polyline points="5 12 12 5 19 12"/>
                                            </svg>
                                        ) : (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="12" y1="5" x2="12" y2="19"/>
                                                <polyline points="19 12 12 19 5 12"/>
                                            </svg>
                                        )}
                                    </div>
                                    <div className="history-item-info">
                                        <span className="history-item-type">
                                            {tx.referenceType === 'quest_gate'
                                                ? t('txType.quest_gate')
                                                : t(`txType.${tx.type}`, tx.type)}
                                        </span>
                                        <span className="history-item-date">{formatDate(tx.createdAt)}</span>
                                        {tx.description && (
                                            <span className="history-item-desc">
                                                {translateDescription(tx)}
                                            </span>
                                        )}
                                    </div>
                                    <div className={`history-item-amount ${tx.amount >= 0 ? 'positive' : 'negative'}`}>
                                        {formatTokenAmount(tx.amount)}
                                    </div>
                                </div>
                            ))}

                            {isLoadingTx && (
                                <div className="history-loading">
                                    <div className="loading-spinner small" />
                                </div>
                            )}

                            {hasMore && !isLoadingTx && (
                                <button
                                    className="load-more-btn"
                                    onClick={() => loadTransactions(false)}
                                >
                                    {t('loadMore')}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Rewarded Ad Player */}
            <RewardedAdPlayer
                isOpen={showAdPlayer}
                onClose={() => setShowAdPlayer(false)}
                onRewardEarned={handleAdRewardEarned}
                adDuration={15}
                skipAfter={5}
                currentBalance={stats?.balance || 0}
                rewardAmount={3}
            />
        </div>
    );
};

export default TokensPage;
